import React from 'react';
import i18next from 'i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import get from 'lodash.get';

import * as CustomPropTypes from 'lib/CustomPropTypes';
import { timeFrom } from 'lib/DateTime';
import Link from 'components/Link';
import StripedEyebrow from 'components/StripedEyebrow';
import Unibrow from 'components/Unibrow';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { HIDE_TIMESTAMP_FOR_BREAKING_BIG_HEADLINE } from 'lib/brandFeatures.json';
import { BigHeadlineTease } from './Tease';

const LAYOUTS = {
  REG_NO_TEASE: 'regNoTease',
  REG_HAS_TEASE: 'regHasTease',
  MASSIVE_NO_TEASE: 'massiveNoTease',
  MASSIVE_HAS_TEASE: 'massiveHasTease',
};

const getLayoutModifier = (headline, showTease) => {
  if (headline.length <= 20) {
    return showTease ? LAYOUTS.MASSIVE_HAS_TEASE : LAYOUTS.MASSIVE_NO_TEASE;
  }

  return showTease ? LAYOUTS.REG_HAS_TEASE : LAYOUTS.REG_NO_TEASE;
};

const publishAgo = (dateCreated, datePublished) => {
  if (!dateCreated) {
    return null;
  }
  const toolkit = 'founders-mono white f2 lh-none mb0 big-headline-updated';

  return (
    <p className={toolkit}>
      {dateCreated !== datePublished ? (
        // TODO: [BENTO-11063] Spanish translation for the word "updated"
        `Updated ${i18next.t('time_ago', { time: timeFrom(datePublished) })}`
      ) : (
        `${i18next.t('time_ago', { time: timeFrom(dateCreated) })}`
      )}
    </p>
  );
};

/**
  BigHeadline fun facts:
  - can't have dek without tease, but tease without dek is okay
  - heading font size varies based on the character count of the heading
  - tease can be video or static image
  - tease vertical position and size (!) depends on whether or not we have a dek
  - on large+ viewports the tease needs to be shifted down
*/

const BigHeadline = (props) => {
  const {
    content: {
      items,
      metadata: { showTimestamp = true, showTeaseImage: showTeaseImageMeta, showDek },
      id,
    },
    pkgClassName,
    vertical,
  } = props;

  const {
    id: contentId,
    item,
    computedValues: {
      headline, url, unibrow, dek, teaseImage, flag,
    } = {},
  } = get(items, '[0]', {});

  const { breakingNews, dateCreated, datePublished } = item || {};

  const showTeaseImage = showTeaseImageMeta && !!teaseImage;
  const hasDek = showTeaseImage && showDek;

  const layoutMod = getLayoutModifier(headline, showTeaseImage);
  const dekMod = showDek ? 'hasDek' : 'noDek';
  const teaseMod = showTeaseImage ? 'has-tease' : 'no-tease';
  const breakingMod = breakingNews ? 'breaking' : 'non-breaking';

  const block = 'big-headline'; // match package name
  const bemElHeadlineText = 'big-headline__text';
  const blockCn = `${block} ${block}--${dekMod} ${block}--${layoutMod} ${block}--${breakingMod} ${block}--${teaseMod}`;

  const sharedColumnClasses = classNames({
    'layout-grid-item grid-col-12 grid-col-6-m df': showTeaseImage,
  });
  const leftColumnClasses = classNames({
    'layout-grid-item flex-column grid-col-12 tc': !showTeaseImage, // no tease image
    'flex-column pr0 pr3-m grid-col-7-l grid-col-6-xl justify-center': hasDek, // tease image & dek
    'flex-column pr0 pr3-m justify-center': showTeaseImage && !hasDek, // tease image & no dek
  });
  const rightColumnClasses = classNames(
    'big-headline__right-column',
    {
      'pl0 pl3-m grid-col-5-l grid-col-6-xl': hasDek, // tease image & dek
      'pl0 pl3-m': showTeaseImage && !hasDek, // tease image & no dek
    },
  );

  const sharedHeadlineCn = 'mb0 mt0 lh-none';
  const headlineTextCn = classNames({
    'f8 f10-m f11-xl': layoutMod === LAYOUTS.REG_NO_TEASE,
    'f8 f9-l': layoutMod === LAYOUTS.REG_HAS_TEASE,
    // massive tease uses negative margin to compensate for lineheight
    'f9 f11-m f12-l f13-xl -mt2-s': layoutMod === LAYOUTS.MASSIVE_NO_TEASE,
    'f9 f11-xl -mt2': layoutMod === LAYOUTS.MASSIVE_HAS_TEASE && showDek,
    'f9 f10-m f11-l f12-xl -mt2-s': layoutMod === LAYOUTS.MASSIVE_HAS_TEASE && !showDek,
  });
  const containerClasses = classNames('big-headline__container', { 'big-headline__container--is-breaking': breakingNews });

  // eslint-disable-next-line max-len
  const hideTimestampWhenBreaking = getFeatureConfigForBrand(HIDE_TIMESTAMP_FOR_BREAKING_BIG_HEADLINE, vertical);

  return (
    <section data-packageid={id} className={`${pkgClassName} ${blockCn} relative`}>
      <div className={containerClasses} data-test="container">
        <div className="layout-grid-container self-center" data-test="inner-container">
          <div className={`${sharedColumnClasses} ${leftColumnClasses}`} data-test="inner-column-container">
            <div className={`ph5 ph0-m ${(breakingNews ? 'h-100 df flex-column' : '')}`}>
              { breakingNews
                && (
                <div className="big-headline__breaking-eyebrow">
                  <StripedEyebrow>{flag || i18next.t('BREAKING')}</StripedEyebrow>
                </div>
                )}
              <div className="pt7 pb7 mt-auto mb-auto" data-contentid={contentId}>
                <Unibrow
                  className={`big-headline__unibrow ${(breakingNews ? 'big-headline__unibrow--breaking' : '')}`}
                  unibrow={unibrow}
                />
                <h2 className={`${bemElHeadlineText} ${sharedHeadlineCn} ${headlineTextCn}`}>
                  <Link to={url}>
                    {headline}
                  </Link>
                </h2>
                { showDek && showTeaseImage
                  && (
                  <p className="big-headline__dek f4 mt3 mb0">
                    {dek}
                  </p>
                  )}
                { showTimestamp && datePublished && !(hideTimestampWhenBreaking && breakingNews)
                  && publishAgo(dateCreated, datePublished, (showTeaseImage && showDek))}
              </div>
            </div>
          </div>
          {showTeaseImage
            && (
            <div className={`${sharedColumnClasses} ${rightColumnClasses}`}>
              <BigHeadlineTease teaseItem={items[0]} />
            </div>
            )}
        </div>
      </div>
    </section>
  );
};

BigHeadline.propTypes = {
  content: CustomPropTypes.package.isRequired,
  vertical: PropTypes.string.isRequired,
  pkgClassName: PropTypes.string,
};

BigHeadline.defaultProps = {
  pkgClassName: 'pkg bigHeadline',
};

export default BigHeadline;
